import i18next from 'i18next';
import { toast } from 'sonner';

import { useWebhookSubscriptionStore } from '@/store/webhook-subscription.store.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

const t = (k, fb) => i18next.t(k, { defaultValue: fb });
const errMsg = (e, fb) => getIntegrationErrorMessage(e) || fb;

export class WebhookSubscriptionIntegration {
    constructor(service) {
        this.service = service;
    }

    async fetchAll(params = {}) {
        const s = useWebhookSubscriptionStore.getState();
        s.setLoading(true);
        try {
            const r = await this.service.getAll(params);
            s.setItems(Array.isArray(r.data) ? r.data : []);
            return { success: true, data: r.data };
        } catch (e) {
            const m = errMsg(e, t('admin:webhooks.messages.loadFailed', { defaultValue: 'Could not load webhook list' }));
            s.setError(m);
            toast.error(m);
            return { success: false, error: m };
        } finally {
            s.setLoading(false);
        }
    }

    async fetchDeliveries(subscriptionId, params = {}) {
        const s = useWebhookSubscriptionStore.getState();
        s.setDeliveriesLoading(true);
        try {
            const r = await this.service.listDeliveries(subscriptionId, params);
            const rows = Array.isArray(r.data) ? r.data : [];
            s.setDeliveries(rows);
            const pag = r.pagination || {};
            s.setDeliveriesPagination({
                page: pag.page || 1,
                pageSize: pag.pageSize || 20,
                total: pag.total || 0,
                totalPages: pag.totalPages || 1,
            });
            return { success: true, data: rows };
        } catch (e) {
            const m = errMsg(e, t('admin:webhooks.deliveries.loadFailed', { defaultValue: 'Could not load deliveries' }));
            toast.error(m);
            return { success: false, error: m };
        } finally {
            s.setDeliveriesLoading(false);
        }
    }

    async create(d) {
        try {
            const r = await this.service.create(d);
            toast.success(t('admin:webhooks.messages.createSuccess', { defaultValue: 'Webhook created' }));
            await this.fetchAll();
            return { success: true, data: r.data };
        } catch (e) {
            const m = errMsg(e, t('admin:webhooks.messages.createFailed', { defaultValue: 'Could not create webhook' }));
            toast.error(m);
            return { success: false, error: m };
        }
    }

    async update(id, d) {
        try {
            const r = await this.service.update(id, d);
            toast.success(t('admin:webhooks.messages.updateSuccess', { defaultValue: 'Webhook updated' }));
            await this.fetchAll();
            return { success: true, data: r.data };
        } catch (e) {
            const m = errMsg(e, t('admin:webhooks.messages.updateFailed', { defaultValue: 'Could not update' }));
            toast.error(m);
            return { success: false, error: m };
        }
    }

    async remove(id) {
        try {
            await this.service.delete(id);
            toast.success(t('admin:webhooks.messages.deleteSuccess', { defaultValue: 'Webhook deleted' }));
            await this.fetchAll();
            return { success: true };
        } catch (e) {
            const m = errMsg(e, t('admin:webhooks.messages.deleteFailed', { defaultValue: 'Could not delete' }));
            toast.error(m);
            return { success: false, error: m };
        }
    }

    async test(id) {
        try {
            const r = await this.service.test(id);
            const payload = r.data || r;
            const result = payload?.result ?? payload;
            useWebhookSubscriptionStore.getState().setTestResult(result);
            toast.success(t('admin:webhooks.messages.testComplete', { defaultValue: 'Test completed' }));
            return { success: true, result };
        } catch (e) {
            const m = errMsg(e, t('admin:webhooks.messages.testFailed', { defaultValue: 'Test failed' }));
            toast.error(m);
            return { success: false, error: m };
        }
    }

    async replayDelivery(subscriptionId, deliveryId) {
        try {
            const r = await this.service.replayDelivery(subscriptionId, deliveryId);
            toast.success(t('admin:webhooks.messages.replaySuccess', { defaultValue: 'Delivery resent' }));
            await this.fetchDeliveries(subscriptionId);
            return { success: true, data: r.data };
        } catch (e) {
            const m = errMsg(e, t('admin:webhooks.messages.replayFailed', { defaultValue: 'Could not resend' }));
            toast.error(m);
            return { success: false, error: m };
        }
    }

    async rotateSecret(id) {
        try {
            const r = await this.service.rotateSecret(id);
            toast.success(t('admin:webhooks.messages.rotateSuccess', { defaultValue: 'Signing key refreshed' }));
            await this.fetchAll();
            return { success: true, data: r.data };
        } catch (e) {
            const m = errMsg(e, t('admin:webhooks.messages.rotateFailed', { defaultValue: 'Could not rotate key' }));
            toast.error(m);
            return { success: false, error: m };
        }
    }
}
